// Thin typed client for the onboarding draft API (ITDEVELOP-3236, integration-api).
// Optimistic concurrency: every draft response carries data.version, and every PUT
// sends it back as a quoted If-Match; a stale version answers 412.
//
// skipToast: the incident axios instance toasts "You dont have access" on every 400
// and 403. Here a 400 is a field-validation answer and a 403/404 a failed draft load,
// both of which the wizard reports itself, so the interceptor's toast is suppressed.
import axios from 'axios';

import type { CreateDraftResponse } from '@/gen/types/CreateDraftResponse';
import type { OnboardingDraftDto } from '@/gen/types/OnboardingDraftDto';
import type { OnboardingRequestRow } from '@/gen/types/OnboardingRequestRow';
import type { ResponseBaseOfCreateDraftResponse } from '@/gen/types/ResponseBaseOfCreateDraftResponse';
import type { ResponseBaseOfOnboardingDraftDto } from '@/gen/types/ResponseBaseOfOnboardingDraftDto';
import type { ListRequestsResponseDto } from '@/gen/types/ListRequestsResponseDto';
import type { ResponseBaseOfListRequestsResponseDto } from '@/gen/types/ResponseBaseOfListRequestsResponseDto';
import type { SaveContractDto } from '@/gen/types/SaveContractDto';
import type { SaveContractsRequestDto } from '@/gen/types/SaveContractsRequestDto';
import type { SaveOrganizationDetailsRequestDto } from '@/gen/types/SaveOrganizationDetailsRequestDto';
import type { ValidationErrorDto } from '@/gen/types/ValidationErrorDto';
import type { ValidationErrorResponse } from '@/gen/types/ValidationErrorResponse';
import { axiosIncidentInstance, makeRequest } from '@/stores/middleware';

const REQUESTS_URL = 'onboarding/requests';

/** Validation fields consumed by form mappers; transport-only metadata stays at the API boundary. */
export type ValidationFieldError = Pick<ValidationErrorDto, 'field' | 'message'>;

function unwrap<T>(data: T | null | undefined, operation: string): T {
    if (data === null || data === undefined) {
        throw new Error(`Onboarding draft API returned no payload for ${operation}.`);
    }
    return data;
}

/** Open a new-organization draft; the server answers its id and initial version. */
export async function createDraft(): Promise<CreateDraftResponse> {
    const response = await makeRequest<ResponseBaseOfCreateDraftResponse>(
        'POST',
        REQUESTS_URL,
        axiosIncidentInstance,
        {},
        { throwOnError: true, skipToast: true },
    );
    return unwrap(response.data?.data, 'createDraft');
}

// The server clamps pageSize to this (ListRequestsHandler.MaxPageSize); asking for more is silently cut.
const REQUESTS_MAX_PAGE_SIZE = 500;

/** One page of onboarding requests (1-based page). */
export async function listRequests(page = 1, pageSize = REQUESTS_MAX_PAGE_SIZE): Promise<ListRequestsResponseDto> {
    const response = await makeRequest<ResponseBaseOfListRequestsResponseDto>(
        'GET',
        REQUESTS_URL,
        axiosIncidentInstance,
        undefined,
        { throwOnError: true, skipToast: true, params: { page, pageSize } },
    );
    return unwrap(response.data?.data, 'listRequests');
}

/**
 * Every onboarding request, for the Organizations screen's requests table. DataTable
 * searches, sorts and pages client-side over whatever it is handed, so the table must hold
 * the full set: this walks the server pages until totalCount is covered (or a page comes
 * back short, which guards against the count moving under us).
 */
export async function listAllRequests(): Promise<ListRequestsResponseDto> {
    const requests: OnboardingRequestRow[] = [];
    let page = 1;
    let response: ListRequestsResponseDto;
    do {
        response = await listRequests(page++, REQUESTS_MAX_PAGE_SIZE);
        requests.push(...response.requests);
    } while (requests.length < Number(response.totalCount) && response.requests.length === REQUESTS_MAX_PAGE_SIZE);
    return { requests, totalCount: Number(response.totalCount) };
}

/** Read a draft, including per-step completeness. */
export async function getDraft(draftId: string): Promise<OnboardingDraftDto> {
    const response = await makeRequest<ResponseBaseOfOnboardingDraftDto>(
        'GET',
        `${REQUESTS_URL}/${draftId}`,
        axiosIncidentInstance,
        undefined,
        { throwOnError: true, skipToast: true },
    );
    return unwrap(response.data?.data, 'getDraft');
}

/** Save the organization-details step; answers the updated draft (with its new version). */
export async function saveOrganizationDetails(
    draftId: string,
    expectedVersion: number,
    details: SaveOrganizationDetailsRequestDto,
): Promise<OnboardingDraftDto> {
    const response = await makeRequest<ResponseBaseOfOnboardingDraftDto>(
        'PUT',
        `${REQUESTS_URL}/${draftId}/organization-details`,
        axiosIncidentInstance,
        details,
        { throwOnError: true, skipToast: true, headers: { 'If-Match': `"${expectedVersion}"` } },
    );
    return unwrap(response.data?.data, 'saveOrganizationDetails');
}

/** Save the footprints-and-contracts step, a full replace of the draft's contract set; answers the updated draft. */
export async function saveContracts(
    draftId: string,
    expectedVersion: number,
    contracts: SaveContractDto[],
): Promise<OnboardingDraftDto> {
    const body: SaveContractsRequestDto = { contracts };
    const response = await makeRequest<ResponseBaseOfOnboardingDraftDto>(
        'PUT',
        `${REQUESTS_URL}/${draftId}/contracts`,
        axiosIncidentInstance,
        body,
        { throwOnError: true, skipToast: true, headers: { 'If-Match': `"${expectedVersion}"` } },
    );
    return unwrap(response.data?.data, 'saveContracts');
}

/** True when a save lost the optimistic-concurrency race (someone else bumped the version). */
export function isVersionConflict(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 412;
}

/**
 * Field-level errors from a 400, or null when the failure is not a validation answer.
 * The onboarding API speaks ValidationErrorResponse ({ errors: [{ field, message }] }),
 * not the ValidationProblemDetails dictionary the incident API uses.
 */
export function getValidationErrors(error: unknown): ValidationErrorDto[] | null {
    if (!axios.isAxiosError(error) || error.response?.status !== 400) return null;
    const errors = (error.response.data as ValidationErrorResponse | undefined)?.errors;
    return Array.isArray(errors) && errors.length > 0 ? errors : null;
}
